import { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { getUnavailablePeriods, getReservations } from "@/services/reservationService";
import type { UnavailablePeriod, Reservation } from "@/types/supabase";
import { DateRange } from "react-day-picker";
import { AlertTriangle } from "lucide-react";

export interface BookingCalendarProps {
  onDatesSelected: (dates: { checkIn: Date; checkOut: Date }) => void;
  adminView?: boolean;
  adminQuoteMode?: boolean; // Shows availability colors but allows selection
  initialCheckIn?: Date;
  initialCheckOut?: Date;
}

const BookingCalendar = ({
  onDatesSelected,
  adminView,
  adminQuoteMode,
  initialCheckIn,
  initialCheckOut,
}: BookingCalendarProps) => {
  const [selected, setSelected] = useState<DateRange>({
    from: initialCheckIn,
    to: initialCheckOut,
  });
  
  const [unavailablePeriods, setUnavailablePeriods] = useState<UnavailablePeriod[]>([]);
  const [confirmedReservations, setConfirmedReservations] = useState<Reservation[]>([]);
  
  useEffect(() => {
    const loadUnavailableDates = async () => {
      try {
        // Charger les périodes indisponibles manuelles
        const periods = await getUnavailablePeriods();
        setUnavailablePeriods(periods);
        
        // Charger toutes les réservations confirmées
        const allReservations = await getReservations();
        const confirmed = allReservations.filter(r => r.status === 'confirmed');
        setConfirmedReservations(confirmed);
        
        console.log('Périodes indisponibles:', periods);
        console.log('Réservations confirmées:', confirmed);
      } catch (error) {
        console.error("Erreur lors du chargement des dates indisponibles:", error);
      }
    };
    
    loadUnavailableDates();
  }, []);
  
  // Helper: check if a date is a check-out only day (can still be used as check-in)
  const isCheckOutOnlyDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // It's a check-out day if it matches the check_out of a confirmed reservation
    const isResCheckOut = confirmedReservations.some(r => format(new Date(r.check_out), 'yyyy-MM-dd') === dateStr);
    const isResCheckIn = confirmedReservations.some(r => format(new Date(r.check_in), 'yyyy-MM-dd') === dateStr);
    const isUnavailEnd = unavailablePeriods.some(p => format(new Date(p.end_date), 'yyyy-MM-dd') === dateStr);
    const isUnavailStart = unavailablePeriods.some(p => format(new Date(p.start_date), 'yyyy-MM-dd') === dateStr);
    
    // A checkout-only day is one that is an end date but NOT a start date of another booking
    return (isResCheckOut && !isResCheckIn && !isUnavailStart) || (isUnavailEnd && !isUnavailStart && !isResCheckIn);
  };

  // Check if a date is fully blocked (not just a checkout day)
  const isFullyBlocked = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check unavailable periods (exclude last day which is checkout)
    const isInUnavail = unavailablePeriods.some(period => {
      const start = new Date(period.start_date);
      const end = new Date(period.end_date);
      return date >= start && date < end; // Exclude the end date itself
    });
    
    // Check confirmed reservations (exclude checkout day)
    const isInReservation = confirmedReservations.some(reservation => {
      const start = new Date(reservation.check_in);
      const end = new Date(reservation.check_out);
      return date >= start && date < end; // Exclude the checkout date itself
    });
    
    return isInUnavail || isInReservation;
  };

  // Combiner les périodes indisponibles manuelles et les réservations confirmées
  // Les jours de départ (check-out) restent sélectionnables comme jour d'arrivée
  const disabledDays = adminQuoteMode 
    ? [{ before: new Date() }]
    : [
        { before: new Date() },
        // Use a matcher function to handle checkout day logic
        (date: Date) => isFullyBlocked(date),
      ];

  // Detect conflicting reservation for selected dates
  const conflictingReservation = useMemo(() => {
    if (!adminQuoteMode || !selected.from || !selected.to) return null;
    
    const selectedFrom = selected.from;
    const selectedTo = selected.to;
    
    // Check if selected range overlaps with any confirmed reservation
    for (const reservation of confirmedReservations) {
      const resFrom = new Date(reservation.check_in);
      const resTo = new Date(reservation.check_out);
      
      // Check for overlap
      if (selectedFrom <= resTo && selectedTo >= resFrom) {
        return reservation;
      }
    }
    
    return null;
  }, [adminQuoteMode, selected.from, selected.to, confirmedReservations]);

  const formatDateRange = (from: Date, to: Date) => {
    return `${format(from, "PP", { locale: fr })} → ${format(to, "PP", { locale: fr })}`;
  };

  return (
    <div className="space-y-4">
      {/* Warning message for conflicting reservation */}
      {conflictingReservation && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">
            Attention : réservation en attente de <strong>{conflictingReservation.first_name} {conflictingReservation.last_name}</strong> du{' '}
            <strong>{format(new Date(conflictingReservation.check_in), "d MMM", { locale: fr })}</strong> au{' '}
            <strong>{format(new Date(conflictingReservation.check_out), "d MMM yyyy", { locale: fr })}</strong>
          </span>
        </div>
      )}
      
      <Calendar
        mode="range"
        selected={selected}
        onSelect={(range) => {
          // Safely handle the DateRange type
          setSelected(range || { from: undefined, to: undefined });
          onDatesSelected({
            checkIn: range?.from,
            checkOut: range?.to
          });
        }}
        disabled={disabledDays}
        numberOfMonths={window.innerWidth > 768 ? 2 : 1}
        className="rounded-md border pointer-events-auto"
        modifiers={{
          booked: (date) => isFullyBlocked(date),
          checkoutDay: (date) => isCheckOutOnlyDay(date),
          available: (date) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date >= today && !isFullyBlocked(date) && !isCheckOutOnlyDay(date);
          },
        }}
        modifiersClassNames={{
          booked: "!bg-red-200 !text-red-800 hover:!bg-red-300 line-through",
          checkoutDay: "!bg-green-100 !text-green-800 hover:!bg-green-200",
          available: "!bg-green-100 !text-green-800 hover:!bg-green-200",
        }}
        classNames={{
          day_today: "bg-villa-sand text-villa-blue font-bold",
          day_selected: "!bg-villa-turquoise !text-white hover:!bg-villa-turquoise hover:!text-white focus:!bg-villa-turquoise focus:!text-white",
          day_range_middle: "!bg-villa-turquoise/20 !text-villa-blue"
        }}
      />

      {/* Légende */}
      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-green-100 border border-green-300"></span>
          <span className="text-muted-foreground">Disponible</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded bg-red-200 border border-red-300"></span>
          <span className="text-muted-foreground">Réservé / Indisponible</span>
        </div>
      </div>
      
      {selected.from && !selected.to && (
        <p className="text-sm text-muted-foreground">
          Sélectionnez votre date de départ
        </p>
      )}
      
      {selected.from && selected.to && (
        <p className="text-sm font-medium text-villa-blue">
          {formatDateRange(selected.from, selected.to)}
        </p>
      )}
    </div>
  );
};

export default BookingCalendar;
